'use client';

import {
  Box,
  Button,
  ButtonProps,
  Flex,
  Text,
  TextProps,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { CgClose, CgMenuRightAlt } from 'react-icons/cg';
import { FaArrowRightLong } from 'react-icons/fa6';
import Logo from '../common/Logo';
import SectionContainer from '../common/SectionContainer';

const navLinks = [
  {
    name: 'Home',
    href: '/greenReturns-2024',
  },
  {
    name: 'agenda',
    href: '/greenReturns-2024/agenda',
  },
  {
    name: 'speakers',
    href: '/greenReturns-2024/speakers',
  },

  {
    name: 'Official Partners',
    href: '/greenReturns-2024/official-partners',
  },
  {
    name: 'Venue',
    href: '/greenReturns-2024/venue',
  },
  {
    name: 'Startups Showcase',
    href: '/greenReturns-2024/startups',
  },
  {
    name: 'Media',
    href: '/greenReturns-2024/media',
  },
  {
    name: 'Contact Us',
    href: '/greenReturns-2024/contact',
  },
  //   {
  //     name: 'Registration',
  //     href: '/greenReturns-2024/register',
  //   },
];

const GreenReturnsNavbar: React.FC<{}> = () => {
  const router = useRouter();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true); // Ensure router.pathname is only used after mount
  }, []);
  return (
    <Box
      width="100%"
      bg="transparent"
      position="fixed"
      top="0"
      left="0"
      right="0"
      pt="30px"
      pb="24px"
      zIndex="99"
      backgroundColor="white"
      boxShadow="md"
    >
      <SectionContainer>
        <Flex
          width="100%"
          align="center"
          justify="space-between"
          columnGap="16px"
        >
          <Button
            variant="unstyled"
            onClick={() => {
              router.push('/');
            }}
          >
            <Box mt={{ xs: '-15px', md: '-20px' }}>
              <Logo />
            </Box>
          </Button>
          {mounted && (
            <Flex
              align="center"
              justify="center"
              rowGap="8px"
              columnGap="24px"
              flexWrap="wrap"
              flexShrink="1"
              display={{ xs: 'none', lg: 'flex' }}
            >
              {navLinks.map((link) => (
                <NavLink
                  key={link?.name}
                  href={link?.href}
                  isActive={
                    router.pathname === link?.href ||
                    (link?.name === 'speakers' &&
                      router.pathname.startsWith(link?.href))
                  }
                  textProps={{
                    color:
                      router.pathname === link?.href ||
                      (link?.name === 'speakers' &&
                        router.pathname.startsWith(link?.href))
                        ? 'black'
                        : 'black',
                    fontWeight:
                      router.pathname === link?.href ||
                      (link?.name === 'speakers' &&
                        router.pathname.startsWith(link?.href))
                        ? '900'
                        : '500',
                  }}
                >
                  {link?.name}
                </NavLink>
              ))}
            </Flex>
          )}
          <Flex gap="16px" align="center">
            {/* <RegisterNowButton
              display={{ xs: 'none', md: 'flex' }}
              variant="outline"
            /> */}
            <NavMenuDropDown />
          </Flex>
        </Flex>
      </SectionContainer>
    </Box>
  );
};

const RegisterNowButton: React.FC<ButtonProps> = (props) => (
  <Link href="/greenReturns-2024/register" passHref>
    <Button
      as="a"
      variant="solid"
      rightIcon={<FaArrowRightLong />}
      textTransform="capitalize"
      fontWeight="black"
      _hover={{
        background: 'darkgreen',
      }}
      flexShrink="0"
      {...props}
      fontSize="14px"
      backgroundColor="#197542"
      color="white"
      // borderColor='primary'
    >
      Register Now
    </Button>
  </Link>
);

const NavMenuDropDown = () => {
  const { onOpen, onClose, isOpen } = useDisclosure();

  const router = useRouter();

  const ref = useRef(null);

  useOutsideClick({
    ref,
    handler: () => {
      if (isOpen) {
        onClose();
      }
    },
  });

  return (
    <Box position="relative" display={{ xs: 'block', lg: 'none' }} ref={ref}>
      <Button
        leftIcon={
          isOpen ? <CgClose size="24px" /> : <CgMenuRightAlt size="24px" />
        }
        variant="outline"
        colorScheme="primary"
        onClick={() => {
          if (isOpen) {
            onClose();
          } else {
            onOpen();
          }
        }}
      >
        Menu
      </Button>
      {isOpen && (
        <Box
          position="absolute"
          width="350px"
          maxWidth="90vw"
          maxHeight="80vh"
          overflowY="auto"
          p="30px"
          pt="24px"
          top="100%"
          right="0"
          bg="white"
          mt="8px"
          borderRadius="20px"
          boxShadow="0 4px 24px 24px rgba(0,0,0,0.1)"
        >
          <Flex
            justify="flex-end"
            borderBottom="1px solid"
            borderColor="gray.200"
            pb="12px"
            mb="12px"
            display={{ xs: 'flex', md: 'none' }}
          >
            {/* <RegisterNowButton /> */}
          </Flex>
          {navLinks.map((link) => {
            const isActive = router.pathname.startsWith(link.href);

            return (
              <Link
                key={link?.name}
                href={link?.href}
                passHref
                onClick={onClose}
              >
                <Box
                  as="a"
                  display="block"
                  p="12px 24px"
                  borderRadius="12px"
                  _hover={{
                    color: 'primary',
                  }}
                  bg={isActive ? 'primaryBg' : 'transparent'}
                  color={isActive ? 'primary' : 'text.common'}
                >
                  <Text
                    fontWeight={isActive ? '900' : '700'}
                    color="inherit"
                    textTransform="capitalize"
                  >
                    {link?.name}
                  </Text>
                </Box>
              </Link>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const NavLink: React.FC<{
  children: React.ReactNode;
  isActive?: boolean;
  href?: string;
  textProps?: TextProps;
}> = ({ children, isActive, href = '#', textProps }) => (
  <Box>
    <Link href={href} passHref>
      <Text
        as="a"
        fontSize=".875rem"
        target={!href.startsWith('/') ? '_blank' : '_self'}
        fontWeight={isActive ? '900' : '600'}
        textTransform="uppercase"
        color="white"
        _hover={{
          textDecoration: 'underline',
        }}
        {...textProps}
      >
        {children}
      </Text>
    </Link>
  </Box>
);

export default GreenReturnsNavbar;
